import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Link,
  Skeleton,
  Typography,
} from "@mui/material";

// Icons
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

import { SEO } from "../../components/common/SEO";
import { Breadcrumb } from "../../components/common/Breadcrumb";
import { CenteredLoading } from "../../components/common/CenteredLoading";
import ToyCard from "../../components/product/ToyCard";
import {
  sellerProductsForProfile,
  SORT_BY,
} from "../../redux/actions/productActions";
import { RootState } from "../../redux/store";
import { Product } from "../../redux/types/productTypes";
import {
  formatDate,
  getInitialWithUpperCase,
  removeDuplicates,
  sendConfirmationEmail,
} from "../../utils";
import { TotalResults } from "../../components/product/TotalResults";
import { DrawerModal } from "../../components/common/DrawerModal";
import Login from "../../components/common/Login";
import SnackbarAlert, {
  SnackbarRef,
} from "../../components/common/SnackbarAlert";
import useFetchUserDetails from "../../hooks/useFetchCurrentUserDetails";
import { MenuList } from "../../components/common/MenuList";
import {
  getSellerPublicLocation,
  SellerPublicLocation,
} from "./seller-setup-api";

const PAGE_SIZE = 18;
const BRAND_ORANGE = "#e78758";
const BRAND_ORANGE_DARK = "#cf7045";

const SellerProfile: React.FC = () => {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [selectedFilter, setSelectedFilter] = useState<SORT_BY>(SORT_BY.LATEST);
  const [isAccountModalOpen, setAccountModalOpen] = useState(false);
  const [isAlertVerifyUserModalOpen, setIsAlertVerifyUserModalOpen] =
    useState(false);

  const snackbarRef = useRef<SnackbarRef>(null);

  const { loading, productsData, error } = useSelector(
    (state: RootState) => state.products,
  );

  const { products = [], sellerInfo } = productsData;
  const { users } = useFetchUserDetails();

  const totalListings = productsData.totalRecords || 0;

  const isSellerVerified = !!sellerInfo?.isVerified;
  const isSellerSetupComplete = sellerInfo?.sellerSetupStatus === "active";
  const verificationChecksCount = [
    isSellerVerified,
    isSellerSetupComplete,
    isSellerSetupComplete,
  ].filter(Boolean).length;

  const [sellerLocation, setSellerLocation] =
    useState<SellerPublicLocation | null>(null);

  /*
   * Only sellers who have finished Stripe onboarding expose a location, and
   * only then is it safe/meaningful to pull city/state from their Stripe account.
   */
  useEffect(() => {
    if (!isSellerSetupComplete || !profileId) {
      setSellerLocation(null);
      return;
    }

    let isCancelled = false;

    getSellerPublicLocation(profileId)
      .then((location) => {
        if (!isCancelled) {
          setSellerLocation(location);
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setSellerLocation(null);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [isSellerSetupComplete, profileId]);

  /*
   * Append each newly fetched page to the existing products while preventing
   * duplicate listings.
   */
  useEffect(() => {
    if (products && products.length > 0) {
      setAllProducts((previousProducts) =>
        removeDuplicates([...previousProducts, ...products], "createdTime"),
      );
    }
  }, [products]);

  /*
   * Fetch the first page whenever the profile or sort option changes.
   */
  useEffect(() => {
    setAllProducts([]);

    if (!profileId) {
      navigate("/");
      return;
    }

    dispatch(
      sellerProductsForProfile(
        Number(profileId),
        "",
        selectedFilter,
        PAGE_SIZE,
      ),
    );
  }, [dispatch, navigate, profileId, selectedFilter]);

  const filterHandler = (value: any) => {
    setSelectedFilter(value as SORT_BY);
  };

  /*
   * Load another page shortly before the visitor reaches the bottom.
   */
  const handleScroll = useCallback(() => {
    const { scrollTop, scrollHeight, clientHeight } = document.documentElement;

    const isNearPageBottom = scrollTop + clientHeight >= scrollHeight - 300;

    if (profileId && isNearPageBottom && products.length > 0 && !loading) {
      const lastProduct = products[products.length - 1];

      dispatch(
        sellerProductsForProfile(
          Number(profileId),
          lastProduct.documentId,
          selectedFilter,
          PAGE_SIZE,
        ),
      );
    }
  }, [dispatch, loading, products, profileId, selectedFilter]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  const handleMailTo = () => {
    if (!users || users.length === 0) {
      setAccountModalOpen(true);
      return;
    }

    if (!users[0].isVerified) {
      setIsAlertVerifyUserModalOpen(true);
      return;
    }

    if (!sellerInfo || !sellerInfo.email) {
      snackbarRef.current?.showSnackbar(
        "This seller’s contact information is currently unavailable.",
        "error",
      );
      return;
    }

    const subject = "Your items on FiddlePiddle";
    const body = "Hello,";

    const mailtoLink = `mailto:${
      sellerInfo.email
    }?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    window.open(mailtoLink, "_blank");
  };

  const handleSendConfirmEmail = async () => {
    try {
      if (users && users.length > 0) {
        await sendConfirmationEmail(users[0].email, users[0].createdTime);

        snackbarRef.current?.showSnackbar(
          "Confirmation email resent. Please check your inbox.",
          "success",
        );

        setIsAlertVerifyUserModalOpen(false);
      }
    } catch (confirmationError) {
      console.error(confirmationError);

      snackbarRef.current?.showSnackbar(
        "We could not resend the confirmation email. Please try again.",
        "error",
      );
    }
  };

  /*
   * Only show the full-page loader during the actual initial request.
   * This prevents sellers with zero listings from being stuck on a loader.
   */
  const isInitialLoading =
    loading && allProducts.length === 0 && (!sellerInfo || !sellerInfo.name);

  if (isInitialLoading) {
    return <CenteredLoading />;
  }

  return (
    <>
      {sellerInfo && sellerInfo.name && (
        <SEO
          title={`${sellerInfo.name}'s Profile • FiddlePiddle • Toys Marketplace`}
          description={`Browse toys and collectibles listed by ${sellerInfo.name} on FiddlePiddle. Discover available toys, protected payments, and verified seller information.`}
          keywordsContent="toys marketplace, buy toys, sell toys, collectible toys, vintage toys, kids toys, toy deals, rare toys, toy shop, toy sellers"
        />
      )}

      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "#f7f8fa",
        }}
      >
        <Box
          sx={{
            position: "relative",
            zIndex: 100,
            backgroundColor: "#ffffff",
          }}
        >
          <Breadcrumb
            items={[
              { text: "Home", url: "/" },
              { text: "Seller Profile", url: "" },
            ]}
          />
        </Box>

        <Container
          maxWidth="xl"
          sx={{
            pt: {
              xs: 2,
              md: 3,
            },
            pb: 3,
          }}
        >
          {/* Seller profile hero */}
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              borderRadius: {
                xs: "22px",
                md: "30px",
              },
              color: "#ffffff",
              background:
                "linear-gradient(135deg, #1d2730 0%, #2a3844 55%, #344754 100%)",
              boxShadow: "0 22px 55px rgba(25, 34, 42, 0.2)",

              "&::before": {
                content: '""',
                position: "absolute",
                width: {
                  xs: "280px",
                  md: "460px",
                },
                height: {
                  xs: "280px",
                  md: "460px",
                },
                top: {
                  xs: "-170px",
                  md: "-250px",
                },
                right: {
                  xs: "-140px",
                  md: "-150px",
                },
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(231,135,88,0.65) 0%, rgba(231,135,88,0) 70%)",
                pointerEvents: "none",
              },

              "&::after": {
                content: '""',
                position: "absolute",
                inset: 0,
                opacity: 0.22,
                backgroundImage: `
                  linear-gradient(
                    rgba(255,255,255,0.08) 1px,
                    transparent 1px
                  ),
                  linear-gradient(
                    90deg,
                    rgba(255,255,255,0.08) 1px,
                    transparent 1px
                  )
                `,
                backgroundSize: "48px 48px",
                maskImage:
                  "linear-gradient(to right, rgba(0,0,0,0.7), transparent)",
                pointerEvents: "none",
              },
            }}
          >
            <Grid
              container
              spacing={{
                xs: 3,
                md: 4,
              }}
              alignItems="center"
              sx={{
                position: "relative",
                zIndex: 1,
                p: {
                  xs: 2.5,
                  sm: 3.5,
                  md: 5,
                },
              }}
            >
              <Grid item xs={12} lg={8}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: {
                      xs: "column",
                      sm: "row",
                    },
                    alignItems: {
                      xs: "center",
                      sm: "flex-start",
                    },
                    gap: {
                      xs: 2,
                      sm: 3,
                    },
                    textAlign: {
                      xs: "center",
                      sm: "left",
                    },
                  }}
                >
                  {sellerInfo && sellerInfo.name ? (
                    <Avatar
                      sx={{
                        width: {
                          xs: 88,
                          sm: 104,
                        },
                        height: {
                          xs: 88,
                          sm: 104,
                        },
                        flexShrink: 0,
                        fontSize: {
                          xs: "2rem",
                          sm: "2.4rem",
                        },
                        fontWeight: 900,
                        color: "#ffffff",
                        background:
                          "linear-gradient(135deg, #f19a70 0%, #d96f40 100%)",
                        border: "5px solid rgba(255,255,255,0.2)",
                        boxShadow: "0 15px 34px rgba(0,0,0,0.28)",
                      }}
                    >
                      {getInitialWithUpperCase(sellerInfo.name)}
                    </Avatar>
                  ) : (
                    <Skeleton
                      variant="circular"
                      width={104}
                      height={104}
                      animation="wave"
                      sx={{
                        bgcolor: "rgba(255,255,255,0.15)",
                      }}
                    />
                  )}

                  <Box
                    sx={{
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    {sellerInfo && sellerInfo.name ? (
                      <>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: {
                              xs: "column",
                              sm: "row",
                            },
                            alignItems: {
                              xs: "center",
                              sm: "center",
                            },
                            gap: 1,
                            mb: 0.75,
                          }}
                        >
                          <Typography
                            component="h1"
                            sx={{
                              fontSize: {
                                xs: "1.9rem",
                                sm: "2.35rem",
                                md: "2.7rem",
                              },
                              lineHeight: 1.1,
                              fontWeight: 900,
                              letterSpacing: "-0.035em",
                              wordBreak: "break-word",
                            }}
                          >
                            {sellerInfo.name}
                          </Typography>

                          {isSellerVerified && isSellerSetupComplete && (
                            <Chip
                              size="small"
                              icon={<ShieldOutlinedIcon />}
                              label="Verified Seller"
                              sx={{
                                color: "#ffffff",
                                bgcolor: "rgba(49, 183, 116, 0.22)",
                                border: "1px solid rgba(111, 225, 164, 0.38)",

                                "& .MuiChip-icon": {
                                  color: "#75dfa9",
                                },
                              }}
                            />
                          )}
                        </Box>

                        <Typography
                          variant="body1"
                          sx={{
                            maxWidth: "640px",
                            mb: 2,
                            color: "rgba(255,255,255,0.72)",
                            lineHeight: 1.6,
                          }}
                        >
                          Browse this seller’s available toys and collectibles,
                          then contact them directly with any questions.
                        </Typography>

                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            justifyContent: {
                              xs: "center",
                              sm: "flex-start",
                            },
                            gap: 1.25,
                            mb: 2.25,
                          }}
                        >
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.75,
                              px: 1.4,
                              py: 0.8,
                              borderRadius: "999px",
                              bgcolor: "rgba(255,255,255,0.09)",
                              border: "1px solid rgba(255,255,255,0.12)",
                            }}
                          >
                            <CalendarMonthRoundedIcon
                              sx={{
                                fontSize: "1rem",
                                color: "#ffad82",
                              }}
                            />

                            <Typography
                              variant="caption"
                              sx={{
                                color: "rgba(255,255,255,0.84)",
                                fontWeight: 650,
                              }}
                            >
                              Member since{" "}
                              {formatDate(sellerInfo.createdTime, false)}
                            </Typography>
                          </Box>

                          {isSellerSetupComplete &&
                            sellerLocation &&
                            (sellerLocation.city || sellerLocation.state) && (
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.75,
                                  px: 1.4,
                                  py: 0.8,
                                  borderRadius: "999px",
                                  bgcolor: "rgba(255,255,255,0.09)",
                                  border: "1px solid rgba(255,255,255,0.12)",
                                }}
                              >
                                <LocationOnOutlinedIcon
                                  sx={{
                                    fontSize: "1rem",
                                    color: "#ffad82",
                                  }}
                                />

                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "rgba(255,255,255,0.84)",
                                    fontWeight: 650,
                                  }}
                                >
                                  {[sellerLocation.city, sellerLocation.state]
                                    .filter(Boolean)
                                    .join(", ")}
                                </Typography>
                              </Box>
                            )}
                        </Box>
                      </>
                    ) : (
                      <>
                        <Skeleton
                          variant="text"
                          width="55%"
                          height={52}
                          animation="wave"
                          sx={{
                            bgcolor: "rgba(255,255,255,0.14)",
                          }}
                        />

                        <Skeleton
                          variant="text"
                          width="80%"
                          height={28}
                          animation="wave"
                          sx={{
                            bgcolor: "rgba(255,255,255,0.1)",
                          }}
                        />

                        <Skeleton
                          variant="text"
                          width="48%"
                          height={28}
                          animation="wave"
                          sx={{
                            bgcolor: "rgba(255,255,255,0.1)",
                          }}
                        />
                      </>
                    )}

                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: {
                          xs: "center",
                          sm: "flex-start",
                        },
                        gap: 1,
                        mb: 2.5,
                      }}
                    >
                      {[
                        { key: "Email", verified: isSellerVerified },
                        { key: "Phone", verified: isSellerSetupComplete },
                        { key: "ID", verified: isSellerSetupComplete },
                      ].map(({ key, verified }) => (
                        <Chip
                          key={key}
                          size="small"
                          label={`${key} ${verified ? "Verified" : "Unverified"}`}
                          icon={
                            verified ? (
                              <CheckCircleRoundedIcon />
                            ) : (
                              <WarningAmberRoundedIcon />
                            )
                          }
                          sx={
                            verified
                              ? {
                                  color: "#ffffff",
                                  bgcolor: "rgba(49, 183, 116, 0.14)",
                                  border: "1px solid rgba(111, 225, 164, 0.2)",

                                  "& .MuiChip-icon": {
                                    color: "#75dfa9",
                                  },
                                }
                              : {
                                  color: "#ffffff",
                                  bgcolor: "rgba(255, 176, 32, 0.14)",
                                  border: "1px solid rgba(255, 193, 82, 0.25)",

                                  "& .MuiChip-icon": {
                                    color: "#ffc152",
                                  },
                                }
                          }
                        />
                      ))}
                    </Box>

                    <Button
                      variant="contained"
                      startIcon={<PersonOutlineOutlinedIcon />}
                      endIcon={<ArrowForwardRoundedIcon />}
                      onClick={handleMailTo}
                      sx={{
                        minHeight: 48,
                        width: {
                          xs: "100%",
                          sm: "auto",
                        },
                        px: 3,
                        borderRadius: "999px",
                        color: "#ffffff",
                        bgcolor: BRAND_ORANGE,
                        fontWeight: 800,
                        textTransform: "none",
                        boxShadow: "0 12px 28px rgba(231,135,88,0.32)",

                        "&:hover": {
                          bgcolor: BRAND_ORANGE_DARK,
                          boxShadow: "0 15px 32px rgba(231,135,88,0.4)",
                          transform: "translateY(-1px)",
                        },
                      }}
                    >
                      Contact User
                    </Button>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} lg={4}>
                <Box
                  sx={{
                    p: {
                      xs: 2.25,
                      sm: 2.75,
                    },
                    borderRadius: "22px",
                    bgcolor: "rgba(255,255,255,0.1)",
                    border: "1px solid rgba(255,255,255,0.15)",
                    backdropFilter: "blur(14px)",
                    boxShadow: "0 18px 40px rgba(0,0,0,0.16)",
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{
                      display: "block",
                      mb: 1.5,
                      color: "rgba(255,255,255,0.62)",
                      fontWeight: 800,
                      letterSpacing: "0.09em",
                    }}
                  >
                    Seller at a glance
                  </Typography>

                  <Grid container spacing={1.5}>
                    <Grid item xs={6}>
                      <Box
                        sx={{
                          height: "100%",
                          p: 1.75,
                          borderRadius: "17px",
                          bgcolor: "rgba(12,18,23,0.28)",
                          border: "1px solid rgba(255,255,255,0.09)",
                        }}
                      >
                        <Inventory2OutlinedIcon
                          sx={{
                            mb: 0.75,
                            color: "#ffad82",
                          }}
                        />

                        <Typography
                          sx={{
                            fontSize: "1.7rem",
                            lineHeight: 1,
                            fontWeight: 900,
                          }}
                        >
                          {totalListings}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            color: "rgba(255,255,255,0.65)",
                          }}
                        >
                          Available listings
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={6}>
                      <Box
                        sx={{
                          height: "100%",
                          p: 1.75,
                          borderRadius: "17px",
                          bgcolor: "rgba(12,18,23,0.28)",
                          border: "1px solid rgba(255,255,255,0.09)",
                        }}
                      >
                        <ShieldOutlinedIcon
                          sx={{
                            mb: 0.75,
                            color: "#75dfa9",
                          }}
                        />

                        <Typography
                          sx={{
                            fontSize: "1.7rem",
                            lineHeight: 1,
                            fontWeight: 900,
                          }}
                        >
                          {verificationChecksCount}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            color: "rgba(255,255,255,0.65)",
                          }}
                        >
                          Verification checks
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Typography
                    variant="body2"
                    sx={{
                      mt: 2,
                      color: "rgba(255,255,255,0.68)",
                      lineHeight: 1.55,
                    }}
                  >
                    Shop confidently and contact the seller before purchasing
                    when you need additional details.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Products heading and sorting */}
          <Box
            sx={{
              mt: 3,
              p: {
                xs: 2,
                sm: 2.5,
              },
              display: "flex",
              flexDirection: {
                xs: "column",
                sm: "row",
              },
              alignItems: {
                xs: "stretch",
                sm: "center",
              },
              justifyContent: "space-between",
              gap: 2,
              borderRadius: "20px",
              bgcolor: "#ffffff",
              border: "1px solid #e6e9ed",
              boxShadow: "0 10px 28px rgba(35, 45, 55, 0.06)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 46,
                  height: 46,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "14px",
                  color: BRAND_ORANGE_DARK,
                  bgcolor: "rgba(231,135,88,0.12)",
                }}
              >
                <StorefrontRoundedIcon />
              </Box>

              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 850,
                    lineHeight: 1.25,
                  }}
                >
                  {sellerInfo && sellerInfo.name
                    ? `Toys from ${sellerInfo.name}`
                    : "Toys from this seller"}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.25,
                    color: "text.secondary",
                  }}
                >
                  Browse all currently available listings.
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: {
                  xs: "space-between",
                  sm: "flex-end",
                },
                gap: 1,
              }}
            >
              <Box
                sx={{
                  color: "text.secondary",
                  fontSize: "0.875rem",
                  whiteSpace: "nowrap",
                }}
              >
                <TotalResults quantity={productsData.totalRecords} />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                }}
              >
                <TuneOutlinedIcon
                  sx={{
                    color: "text.secondary",
                    fontSize: "1.25rem",
                  }}
                />

                <MenuList
                  menuItems={[
                    {
                      label: "Sort by:",
                      value: "",
                      isDisabled: true,
                    },
                    {
                      label: "Latest",
                      value: SORT_BY.LATEST,
                    },
                    {
                      label: "Popularity",
                      value: SORT_BY.POPULAR,
                    },
                    {
                      label: "Lowest Price",
                      value: SORT_BY.LOWEST_PRICE,
                    },
                    {
                      label: "Highest Price",
                      value: SORT_BY.HIGHEST_PRICE,
                    },
                  ]}
                  filterHandler={filterHandler}
                  defaultValue={selectedFilter}
                />
              </Box>
            </Box>
          </Box>
        </Container>

        {/* Product grid */}
        <Box
          sx={{
            py: {
              xs: 3,
              md: 4,
            },
            bgcolor: "#eef1f4",
            borderTop: "1px solid #e1e5e9",
          }}
        >
          <Container maxWidth="xl">
            {error && !loading && allProducts.length === 0 ? (
              <Box
                sx={{
                  maxWidth: 620,
                  mx: "auto",
                  p: {
                    xs: 3,
                    sm: 4,
                  },
                  textAlign: "center",
                  borderRadius: "22px",
                  bgcolor: "#ffffff",
                  border: "1px solid #e3e7eb",
                }}
              >
                <StorefrontRoundedIcon
                  sx={{
                    mb: 1.5,
                    fontSize: "3rem",
                    color: BRAND_ORANGE,
                  }}
                />

                <Typography
                  variant="h6"
                  sx={{
                    mb: 0.75,
                    fontWeight: 850,
                  }}
                >
                  We could not load these listings
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Please refresh the page and try again.
                </Typography>
              </Box>
            ) : loading && allProducts.length === 0 ? (
              <Grid container spacing={2}>
                {Array.from({ length: 6 }).map((_, index) => (
                  <Grid
                    item
                    key={`seller-product-skeleton-${index}`}
                    xs={6}
                    sm={4}
                    md={3}
                    lg={2}
                  >
                    <Box
                      sx={{
                        overflow: "hidden",
                        borderRadius: "18px",
                        bgcolor: "#ffffff",
                        border: "1px solid #e2e6ea",
                      }}
                    >
                      <Skeleton
                        variant="rectangular"
                        height={190}
                        animation="wave"
                      />

                      <Box sx={{ p: 1.5 }}>
                        <Skeleton variant="text" width="90%" height={26} />
                        <Skeleton variant="text" width="60%" height={22} />
                        <Skeleton variant="text" width="35%" height={28} />
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            ) : allProducts.length > 0 ? (
              <Grid container spacing={2}>
                {allProducts.map((product, index) => (
                  <Grid
                    item
                    key={product.documentId || product.createdTime || index}
                    xs={6}
                    sm={4}
                    md={3}
                    lg={2}
                  >
                    <ToyCard product={product} loading={loading} />
                  </Grid>
                ))}

                {loading && allProducts.length >= PAGE_SIZE && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        pt: 2,
                        pb: 1,
                      }}
                    >
                      <CircularProgress
                        size={34}
                        sx={{
                          color: BRAND_ORANGE,
                        }}
                      />
                    </Box>
                  </Grid>
                )}
              </Grid>
            ) : (
              <Box
                sx={{
                  maxWidth: 660,
                  mx: "auto",
                  px: {
                    xs: 2.5,
                    sm: 4,
                  },
                  py: {
                    xs: 5,
                    sm: 6,
                  },
                  textAlign: "center",
                  borderRadius: "24px",
                  bgcolor: "#ffffff",
                  border: "1px solid #e1e5e9",
                  boxShadow: "0 15px 34px rgba(35,45,55,0.06)",
                }}
              >
                <Box
                  sx={{
                    width: 76,
                    height: 76,
                    mx: "auto",
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "22px",
                    color: BRAND_ORANGE_DARK,
                    bgcolor: "rgba(231,135,88,0.12)",
                  }}
                >
                  <StorefrontRoundedIcon
                    sx={{
                      fontSize: "2.4rem",
                    }}
                  />
                </Box>

                <Typography
                  variant="h5"
                  sx={{
                    mb: 1,
                    fontWeight: 850,
                  }}
                >
                  No toys listed yet
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    maxWidth: 460,
                    mx: "auto",
                    color: "text.secondary",
                    lineHeight: 1.65,
                  }}
                >
                  This seller does not currently have any active listings. Check
                  back later to see what they add.
                </Typography>
              </Box>
            )}
          </Container>
        </Box>
      </Box>

      <SnackbarAlert ref={snackbarRef} />

      <DrawerModal
        isOpen={isAccountModalOpen}
        onClose={() => {
          setAccountModalOpen(false);
        }}
        anchor="bottom"
        onOpen={() => {}}
      >
        <Login
          openFormFrom="User"
          alertMessage="You must be signed in to perform that action."
          onLogin={() => {
            setAccountModalOpen(false);

            snackbarRef.current?.showSnackbar(
              "Signed in successfully",
              "success",
            );
          }}
          isOpenFromOpenOtherPage
        />
      </DrawerModal>

      <DrawerModal
        isOpen={isAlertVerifyUserModalOpen}
        onClose={() => {
          setIsAlertVerifyUserModalOpen(false);
        }}
        anchor="bottom"
        onOpen={() => {}}
      >
        <Box
          sx={{
            p: 1,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              lineHeight: 1.65,
            }}
          >
            You must confirm your account to continue. Look for the confirmation
            email in your inbox, or{" "}
            <Link
              className="clickableLink"
              onClick={handleSendConfirmEmail}
              sx={{
                cursor: "pointer",
                fontWeight: 700,
              }}
            >
              click here
            </Link>{" "}
            to resend it.
          </Typography>
        </Box>
      </DrawerModal>
    </>
  );
};

export default SellerProfile;
